
import React, { useEffect, useState } from 'react';
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Typography,
  Box,
  Tooltip,
  IconButton,
  InputLabel
} from '@mui/material';
import ReportOutlinedIcon from '@mui/icons-material/ReportOutlined';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams } from 'react-router-dom';

interface ContentProps {
  dynamicRows: any;
  setDynamicRows: (rows: any[]) => void;
  categoryError: string;
  setCategoryError: (error: string) => void;
  sendTotalFee: (totalFee: number) => void;
  setApplicationFee: (string) => void;
  ApplicationFee: string;
  ApplicationStatus: string;
  applicationID?: string;
  handleGetApplicationDetails?: any;
}

const DynamicContent: React.FC<ContentProps> = ({
  dynamicRows,
  setDynamicRows,
  categoryError,
  setCategoryError,
  sendTotalFee,
  ApplicationFee,
  ApplicationStatus,
  setApplicationFee,
  applicationID,
  handleGetApplicationDetails
}) => {
  const [categories, setCategories] = useState<any[]>([]);
  const { permit } = useParams();

  // Fetch category list along with SubscriptionFee
  const fetchCategories = async () => {
    try {
      const response = await httpRequest(
        'GET',
        `/configuration/vendingAmusement/${permit}`
      );
      setCategories(response?.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [permit]);

  // Synchronize dynamicRows with dynamicRows when dynamicRows changes
  useEffect(() => {
    setApplicationFee(ApplicationFee);
    if (!dynamicRows || dynamicRows?.length === 0) {
      setDynamicRows([
        { MachineName: '', Location: '', Fee: 0, MachineID: '' }
      ]);
    }
  }, [dynamicRows]);

  // Handle row change
  const handleRowChange = (index: number, field: string, value: any) => {
      const updatedRows = [...dynamicRows];
      const updatedRow = { ...updatedRows[index] };
      let hasChanges = false;

      if (field === 'MachineName') {
        const selectedCategory = categories.find(
          (category) => category.CategoryName === value
        );
        updatedRow.MachineName = value;
        updatedRow.Fee = selectedCategory ? selectedCategory.Fee : 0;
        updatedRow.MachineID = selectedCategory ? selectedCategory.ID : '';
        updatedRow.ID = updatedRow.ID || selectedCategory?.ID;
        hasChanges = true;
      } else {
        updatedRow[field] = value;
        hasChanges = true;
      }

      if (hasChanges) {
        updatedRow.changed = true;
      }

      updatedRows[index] = updatedRow;

      // Validate rows
      const isValid = updatedRows.every(
        (row) =>
          (row.MachineName && row.Location && row.Fee > 0) ||
          (!row.MachineName && !row.Location && row.Fee === 0)
      );

      setCategoryError(isValid ? '' : 'Please add location.');

      // Calculate and send total fee
      const total = updatedRows.reduce(
        (sum, row) => sum + (Number(row.Fee) || 0),
        0
      );
      sendTotalFee(total);

      setDynamicRows(updatedRows);
    }

    // Method to remove row
  const removeRow = async (index: number) => {
      if (ApplicationStatus === 'Draft') {
        const rowToDelete = dynamicRows[index];
        try {
          const response = await httpRequest(
            'DELETE',
            `/clerk_vending/vendingMachine/${rowToDelete.ID}`
          );
          if (response.status === 'success') {
            toast.success('Machine deleted successfully.');
            setDynamicRows(dynamicRows.filter((_, i) => i !== index));
          }
        } catch (error) {
          console.error('Error deleting row:', error);
        }
      } else {
        setDynamicRows(dynamicRows.filter((_, i) => i !== index));
      }
    }

  //Method to edit location
  const handleEditRow = async (index: number) => {
    const rowToEdit = dynamicRows[index];

    if (rowToEdit.changed) {
      const updatedRow = {
        Fee: rowToEdit.Fee,
        Location: rowToEdit.Location,
        MachineID: rowToEdit.MachineID,
        MachineName: rowToEdit.MachineName,
        RowStatus: rowToEdit.RowStatus
      };
      try {
        const response = await httpRequest(
          'PATCH',
          `/clerk_vending/vendingMachine/update/${rowToEdit.ID}`,
          updatedRow
        );
        if (response.status === 'success') {
          toast.success('Location updated successfully.');
          const updatedRows = [...dynamicRows];
          updatedRows[index] = { 
            ...updatedRow,
            ID: rowToEdit.ID
           };
          updatedRows[index].changed = false;
          setDynamicRows(updatedRows);
        } else {
        }
      } catch (error) {}
    } else {
      toast.error('No changes made in location field');
    }
  };

  //Method to add location
  const handleAddRow = async (row) => {
    const rowToEdit = dynamicRows[row];
    if (!rowToEdit.MachineName || !rowToEdit.Location) {
      setCategoryError('Machine Name and Location are required.');
      return;
    }
    const updatedRow = {
      Fee: rowToEdit.Fee,
      Location: rowToEdit.Location,
      MachineID: rowToEdit.MachineID,
      MachineName: rowToEdit.MachineName,
      RowStatus: rowToEdit.RowStatus
    };
    try {
      const response = await httpRequest(
        'POST',
        `/clerk_vending/vendingMachine/insert/${applicationID}`,
        updatedRow
      );
      if (response.status === 'success') {
        toast.success('Machine details added successfully.');
        handleGetApplicationDetails();
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  //Method to calculate total fee
  const CalculateTotalFee = () => {
    return dynamicRows
      .reduce((total, row) => total + (Number(row.Fee) || 0), 0)
      .toFixed(2);
  };

  return (
    <Box mb={5}>
      <Box display="flex">
        <Typography variant="h5" mt={2}>
          {ApplicationStatus === 'In Review'
            ? 'Update Machine Details'
            : ApplicationStatus === 'Draft' || !ApplicationStatus
            ? 'Add Machine Details'
            : (ApplicationStatus === 'Approved' ||
                ApplicationStatus === 'Rejected') &&
              'Machine Details'}
        </Typography>

        {dynamicRows?.length && !ApplicationStatus && (
          <Tooltip
            title="Please add at least one machine & location to proceed"
            placement="right"
            arrow
          >
            <IconButton
              sx={{
                ml: 1,
                mt: 1,
                color: 'warning.main',
                '&:hover': { color: 'warning.dark' }
              }}
            >
              <ReportOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box mt={3}>
        {categoryError && (
          <Typography color="#8B0000" sx={{ fontSize: '15px' }}>
            {categoryError}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          width: { xs: '100%', sm: '100%', md: '50%', lg: '50%' },
          mt: 2
        }}
      >
        {dynamicRows?.length > 0 && (
          <TableContainer sx={{ mt: 2, p: 0 }}>
            <Table>
              <TableBody>
                {ApplicationStatus !== 'In Review' && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      {ApplicationStatus === 'Draft' || !ApplicationStatus ? (
                        <Button
                          variant="contained"
                          sx={{
                            height: '30px',
                            fontSize: '12px'
                          }}
                          onClick={ () => {
                            if (
                              dynamicRows.length === 0 ||
                              (dynamicRows[0]?.Location &&
                                dynamicRows[0]?.MachineName)
                            ) {

                              setDynamicRows([
                                {
                                  MachineName: '',
                                  Location: '',
                                  Fee: 0,
                                  MachineID: '',
                                  Add: true
                                },
                                ...dynamicRows
                              ]);
                            } else {
                              setCategoryError(
                                'Machine Name and Location are required.'
                              );
                            }
                          }}
                        >
                          Add
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                )}

                {/* Rows loop */}
                {dynamicRows?.map((row, index) => (
                  <TableRow
                    key={
                      `${row?.MachineName || 'emptyRow'}-${index}`
                    }
                    sx={{
                      borderBottom: '1px solid rgba(224, 224, 224, 1)'
                    }}
                  >
                    <TableCell
                      sx={{
                        borderBottom: 'none',
                        width: {
                          xs: '100%',
                          sm: '30%',
                          pb: { xs: 1.5, sm: 'auto' }
                        }
                      }}
                    >
                      <FormControl fullWidth size="medium">
                        <InputLabel>Select Machine</InputLabel>
                        <Select
                          value={row?.MachineName}
                          onChange={(e) =>
                            handleRowChange(
                              index,
                              'MachineName',
                              e.target.value
                            )
                          }
                          label="Select Machine"
                          disabled={
                            ApplicationStatus && ApplicationStatus !== 'Draft'
                          }
                        >
                          <MenuItem value="0" disabled>
                            Select Machine
                          </MenuItem>
                          {categories.map((category) => (
                            <MenuItem
                              key={category.ID}
                              value={category.CategoryName}
                            >
                              {category.CategoryName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: 'none',
                        width: { xs: '100%', sm: '35%' },
                        py: { xs: 1, sm: 'auto' }
                      }}
                    >
                      <TextField
                        size="medium"
                        label="Location"
                        value={row.Location}
                        variant="outlined"
                        onChange={(e) =>
                          handleRowChange(index, 'Location', e.target.value)
                        }
                        placeholder="Enter location"
                        fullWidth
                        disabled={
                          ApplicationStatus === 'Approved' ||
                          ApplicationStatus === 'Rejected'
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      {row.MachineName ? (
                        <Typography variant="body2" color="textSecondary">
                          ${Number(row.Fee).toFixed(2)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          $0.00
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      sx={{ borderBottom: 'none', textAlign: 'center' }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        {!ApplicationStatus ||
                        (ApplicationStatus === 'Draft' && !row?.Add) ? (
                          <Box>
                            {ApplicationStatus && (
                              <Button
                                type="button"
                                onClick={() => handleEditRow(index)}
                                variant="outlined"
                                title="Edit"
                                color="primary"
                                disabled={!row.changed || !row.Location}
                                size="small"
                                sx={{
                                  minWidth: '15px',
                                  minHeight: '15px',
                                  padding: '3px',
                                  borderRadius: '50%',

                                  backgroundColor:
                                    !row.changed || !row.Location
                                      ? '#3328281f'
                                      : 'transparent',
                                  borderColor:
                                    !row.changed || !row.Location
                                      ? '#3328281f'
                                      : '',
                                  '&:hover': {
                                    backgroundColor:
                                      !row.changed || !row.Location
                                        ? '#3328281f'
                                        : 'transparent'
                                  }
                                }}
                              >
                                <EditIcon color="primary" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              onClick={() => removeRow(index)}
                              variant="outlined"
                              color="error"
                              size="small"
                              sx={{
                                ml: 2,
                                minWidth: '18px',
                                minHeight: '18px',
                                padding: '2px',
                                borderRadius: '50%'
                              }}
                            >
                              <DeleteIcon color="error" />
                            </Button>
                          </Box>
                        ) : ApplicationStatus === 'Draft' && row?.Add ? (
                          <Button
                            type="button"
                            onClick={() => handleAddRow(index)}
                            variant="outlined"
                            color="primary"
                            size="small"
                            sx={{
                              ml: 5,
                              minWidth: '18px',
                              minHeight: '18px',
                              padding: '2px',
                              borderRadius: '50%'
                            }}
                            title="Add Machine Details"
                          >
                            <AddIcon color="primary" />
                          </Button>
                        ) : ApplicationStatus === 'In Review' ||
                          ApplicationStatus === 'Draft' ? (
                          <Button
                            type="button"
                            onClick={() => handleEditRow(index)}
                            variant="outlined"
                            title="Edit"
                            color="primary"
                            disabled={!row.changed || !row.Location}
                            size="small"
                            sx={{
                              minWidth: '15px',
                              minHeight: '15px',
                              padding: '3px',
                              borderRadius: '50%',

                              backgroundColor:
                                !row.changed || !row.Location
                                  ? '#3328281f'
                                  : 'transparent',
                              borderColor:
                                !row.changed || !row.Location
                                  ? '#3328281f'
                                  : '',
                              '&:hover': {
                                backgroundColor:
                                  !row.changed || !row.Location
                                    ? '#3328281f'
                                    : 'transparent'
                              }
                            }}
                          >
                            <EditIcon color="primary" />
                          </Button>
                        ) : (
                          ''
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow
                  sx={{ borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                >
                  <TableCell></TableCell>
                  <TableCell>
                    {dynamicRows?.length > 0 && (
                      <Typography variant="h4">Total Fee</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {dynamicRows?.length > 0 && (
                      <Typography variant="h5">
                        ${CalculateTotalFee()}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default DynamicContent;
